import { create } from 'zustand';
import type { MasterLayoutId, Slide, SlideElement } from '../types/slide';
import {
  applyPayloadToSlide,
  buildLayout,
  createSmartBlock,
  createStarterDeck,
  hydrateMagicDeck,
  renumberSlides,
  slideToPayload,
  switchLayout,
} from '../lib/layouts';
import { uid } from '../lib/id';
import { ApiError, api, type SlideAiAction, type Visibility } from '../lib/api';
import { THEMES } from '../lib/brand';
import type { MagicSlidePayload } from '../types/slide';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'offline' | 'error' | 'conflict';

interface HistorySnap {
  slides: Slide[];
  title: string;
}

interface DeckState {
  deckId: string | null;
  title: string;
  theme: string;
  visibility: Visibility;
  category: string;
  /** False for decks shared by someone else: viewable, never saved. */
  canEdit: boolean;
  /** Owner of the open deck (co-editors can edit but not share or manage editors). */
  isOwner: boolean;
  ownerName: string;
  slides: Slide[];
  activeSlideIndex: number;
  /** Multi-select; last id is the primary (handles / inspector). */
  selectedElementIds: string[];
  /** Convenience: last selected id, or null. */
  selectedElementId: string | null;
  saveStatus: SaveStatus;
  lastSavedAt: string | null;
  past: HistorySnap[];
  future: HistorySnap[];
  presenting: boolean;
  /** Canvas zoom; null = fit to viewport. */
  zoom: number | null;
  /** Scale the canvas is actually rendered at (fit or zoom). */
  viewScale: number;

  setTitle: (title: string) => void;
  setTheme: (theme: string) => void;
  /** Change who can see the deck and its category; saved immediately. */
  setSharing: (visibility: Visibility, category: string) => Promise<void>;
  setActiveSlide: (index: number) => void;
  /** Pass `additive` (Shift/⌘-click) to toggle membership in the selection. */
  selectElement: (id: string | null, opts?: { additive?: boolean }) => void;
  setPresenting: (v: boolean) => void;
  setZoom: (zoom: number | null) => void;
  setViewScale: (scale: number) => void;
  /** Solid color; pass an image URL to use a photo background instead. */
  setSlideBackground: (color: string, image?: string) => void;

  /**
   * Snapshot for undo. Calls with the same `group` within a short window collapse
   * into one step (typing, nudging, dragging a field).
   */
  pushHistory: (group?: string) => void;
  undo: () => void;
  redo: () => void;

  updateElement: (elementId: string, patch: Partial<SlideElement>) => void;
  /** Move several unlocked elements by the same delta (group drag). */
  nudgeElements: (ids: string[], dx: number, dy: number) => void;
  addElement: (el: SlideElement) => void;
  removeElement: (elementId: string) => void;
  removeSelectedElements: () => void;
  duplicateElement: (elementId: string) => void;
  /** Align one selected element to the slide content area, or several relative to each other. */
  alignSelected: (edge: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => void;
  distributeSelected: (axis: 'horizontal' | 'vertical') => void;
  /** Copy visual style from the primary selection onto the other selected elements. */
  duplicateStyles: () => void;
  applyLayout: (layoutId: MasterLayoutId) => void;
  setSlideNotes: (notes: string) => void;
  applySlideAi: (action: SlideAiAction) => Promise<void>;

  addSlide: (layoutId?: MasterLayoutId) => void;
  duplicateSlide: (index?: number) => void;
  deleteSlide: (index?: number) => void;
  reorderSlides: (from: number, to: number) => void;
  replaceSlides: (slides: Slide[], title?: string) => void;
  /** Rebuild every slide in the current AST template, keeping its content. */
  reapplyTemplate: () => void;

  syncSave: (opts?: { force?: boolean; saveVersion?: boolean }) => Promise<void>;
  /** Reload from server after a conflict (discards local unsaved edits). */
  reloadFromServer: () => Promise<void>;
  restoreVersion: (versionId: string) => Promise<void>;
  loadOrCreate: () => Promise<void>;
  openDeck: (id: string) => Promise<void>;
  /** Creates and saves a new deck; resolves with its id (null when offline). */
  newDeck: () => Promise<string | null>;
  /** Create a deck from a builtin or API template. */
  newDeckFromTemplate: (slides: Slide[], title: string, theme?: string) => Promise<string | null>;
  /** Copies a deck (yours or shared) into a new private deck you own; resolves with its id. */
  duplicateDeck: (id: string) => Promise<string>;
  deleteDeck: (id: string) => Promise<void>;
  /** `replace` swaps the whole deck; `append` inserts the drafted content slides after the current one. */
  applyMagicDraft: (notes: string, mode?: 'replace' | 'append') => Promise<void>;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let lastHistory: { group: string; at: number } | null = null;
const HISTORY_GROUP_MS = 1000;

/** Content area used for horizontal alignment (left of the template's image strip). */
const ALIGN_AREA = { left: 52, right: 1127, top: 0, bottom: 673 } as const;

/** Write any pending autosave now (before switching decks). */
async function flushSave(get: () => DeckState) {
  if (!saveTimer) return;
  clearTimeout(saveTimer);
  saveTimer = null;
  await get().syncSave();
}

function freshDeckState() {
  return {
    deckId: null,
    title: 'Untitled deck',
    theme: 'ast_standard',
    visibility: 'private' as Visibility,
    category: 'general',
    canEdit: true,
    isOwner: true,
    ownerName: '',
    slides: createStarterDeck(),
    activeSlideIndex: 0,
    selectedElementIds: [] as string[],
    selectedElementId: null as string | null,
    saveStatus: 'idle' as SaveStatus,
    lastSavedAt: null,
    past: [],
    future: [],
  };
}

const STYLE_KEYS: (keyof SlideElement)[] = [
  'fontSize',
  'fontWeight',
  'font',
  'color',
  'align',
  'fill',
  'stroke',
  'radius',
];

function selectionState(ids: string[]) {
  return {
    selectedElementIds: ids,
    selectedElementId: ids.length ? ids[ids.length - 1]! : null,
  };
}

function cloneSlides(slides: Slide[]): Slide[] {
  return structuredClone(slides);
}

export const useDeckStore = create<DeckState>((set, get) => ({
  deckId: null,
  title: 'Weekly Leadership Update',
  theme: 'ast_standard',
  visibility: 'private',
  category: 'general',
  canEdit: true,
  isOwner: true,
  ownerName: '',
  slides: createStarterDeck(),
  activeSlideIndex: 0,
  selectedElementIds: [],
  selectedElementId: null,
  saveStatus: 'idle',
  lastSavedAt: null,
  past: [],
  future: [],
  presenting: false,
  zoom: null,
  viewScale: 0.7,

  setTitle: (title) => {
    get().pushHistory('title');
    set({ title });
    scheduleSave(get);
  },

  setTheme: (theme) => {
    set({ theme });
    scheduleSave(get);
  },

  setSharing: async (visibility, category) => {
    set({ visibility, category });
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = null;
    await get().syncSave();
  },

  setActiveSlide: (index) => set({ activeSlideIndex: index, ...selectionState([]) }),

  selectElement: (id, opts) => {
    if (id === null) {
      set(selectionState([]));
      return;
    }
    if (opts?.additive) {
      const cur = get().selectedElementIds;
      const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
      set(selectionState(next));
      return;
    }
    set(selectionState([id]));
  },

  setPresenting: (v) => set({ presenting: v }),

  setZoom: (zoom) => set({ zoom }),

  setViewScale: (viewScale) => set({ viewScale }),

  setSlideBackground: (color, image) => {
    get().pushHistory();
    const { slides, activeSlideIndex } = get();
    const next = cloneSlides(slides);
    next[activeSlideIndex].background = color;
    next[activeSlideIndex].backgroundImage = image;
    set({ slides: next });
    scheduleSave(get);
  },

  pushHistory: (group) => {
    const now = Date.now();
    if (group && lastHistory?.group === group && now - lastHistory.at < HISTORY_GROUP_MS) {
      lastHistory.at = now;
      return;
    }
    lastHistory = group ? { group, at: now } : null;
    const { slides, title, past } = get();
    set({
      past: [...past.slice(-40), { slides: cloneSlides(slides), title }],
      future: [],
    });
  },

  undo: () => {
    const { past, slides, title, future } = get();
    if (!past.length) return;
    const prev = past[past.length - 1];
    set({
      past: past.slice(0, -1),
      future: [{ slides: cloneSlides(slides), title }, ...future],
      slides: prev.slides,
      title: prev.title,
    });
    scheduleSave(get);
  },

  redo: () => {
    const { future, slides, title, past } = get();
    if (!future.length) return;
    const next = future[0];
    set({
      future: future.slice(1),
      past: [...past, { slides: cloneSlides(slides), title }],
      slides: next.slides,
      title: next.title,
    });
    scheduleSave(get);
  },

  updateElement: (elementId, patch) => {
    const { slides, activeSlideIndex } = get();
    const next = cloneSlides(slides);
    const el = next[activeSlideIndex].elements.find((e) => e.id === elementId);
    if (!el || el.locked) return;
    Object.assign(el, patch);
    set({ slides: next });
    scheduleSave(get);
  },

  nudgeElements: (ids, dx, dy) => {
    if (!ids.length || (dx === 0 && dy === 0)) return;
    const { slides, activeSlideIndex } = get();
    const next = cloneSlides(slides);
    const idSet = new Set(ids);
    for (const el of next[activeSlideIndex].elements) {
      if (!idSet.has(el.id) || el.locked) continue;
      el.x += dx;
      el.y += dy;
    }
    set({ slides: next });
    scheduleSave(get);
  },

  addElement: (el) => {
    get().pushHistory();
    const { slides, activeSlideIndex } = get();
    const next = cloneSlides(slides);
    next[activeSlideIndex].elements.push(el);
    set({ slides: next, ...selectionState([el.id]) });
    scheduleSave(get);
  },

  removeElement: (elementId) => {
    get().pushHistory();
    const { slides, activeSlideIndex, selectedElementIds } = get();
    const next = cloneSlides(slides);
    next[activeSlideIndex].elements = next[activeSlideIndex].elements.filter(
      (e) => e.id !== elementId || e.locked,
    );
    set({
      slides: next,
      ...selectionState(selectedElementIds.filter((id) => id !== elementId)),
    });
    scheduleSave(get);
  },

  removeSelectedElements: () => {
    const { selectedElementIds, slides, activeSlideIndex } = get();
    const unlocked = selectedElementIds.filter((id) => {
      const el = slides[activeSlideIndex]?.elements.find((e) => e.id === id);
      return el && !el.locked;
    });
    if (!unlocked.length) return;
    get().pushHistory();
    const next = cloneSlides(slides);
    const drop = new Set(unlocked);
    next[activeSlideIndex].elements = next[activeSlideIndex].elements.filter((e) => !drop.has(e.id) || e.locked);
    set({ slides: next, ...selectionState([]) });
    scheduleSave(get);
  },

  duplicateElement: (elementId) => {
    const { slides, activeSlideIndex } = get();
    const el = slides[activeSlideIndex]?.elements.find((e) => e.id === elementId);
    if (!el || el.locked) return;
    get().addElement({ ...structuredClone(el), id: uid(), x: el.x + 24, y: el.y + 24 });
  },

  alignSelected: (edge) => {
    const { slides, activeSlideIndex, selectedElementIds } = get();
    const els = slides[activeSlideIndex]?.elements.filter(
      (e) => selectedElementIds.includes(e.id) && !e.locked,
    );
    if (!els?.length) return;
    get().pushHistory();
    const next = cloneSlides(slides);
    const targets = next[activeSlideIndex].elements.filter((e) => selectedElementIds.includes(e.id) && !e.locked);

    if (targets.length === 1) {
      const el = targets[0]!;
      const a = ALIGN_AREA;
      if (edge === 'left') el.x = a.left;
      else if (edge === 'center') el.x = Math.round(a.left + (a.right - a.left - el.w) / 2);
      else if (edge === 'right') el.x = a.right - el.w;
      else if (edge === 'top') el.y = 100;
      else if (edge === 'middle') el.y = Math.round((a.bottom - el.h) / 2);
      else el.y = a.bottom - 24 - el.h;
    } else {
      const minX = Math.min(...targets.map((e) => e.x));
      const maxX = Math.max(...targets.map((e) => e.x + e.w));
      const minY = Math.min(...targets.map((e) => e.y));
      const maxY = Math.max(...targets.map((e) => e.y + e.h));
      for (const el of targets) {
        if (edge === 'left') el.x = minX;
        else if (edge === 'center') el.x = Math.round(minX + (maxX - minX - el.w) / 2);
        else if (edge === 'right') el.x = maxX - el.w;
        else if (edge === 'top') el.y = minY;
        else if (edge === 'middle') el.y = Math.round(minY + (maxY - minY - el.h) / 2);
        else el.y = maxY - el.h;
      }
    }
    set({ slides: next });
    scheduleSave(get);
  },

  distributeSelected: (axis) => {
    const { slides, activeSlideIndex, selectedElementIds } = get();
    const targets = slides[activeSlideIndex]?.elements
      .filter((e) => selectedElementIds.includes(e.id) && !e.locked)
      .slice()
      .sort((a, b) => (axis === 'horizontal' ? a.x - b.x : a.y - b.y));
    if (!targets || targets.length < 3) return;
    get().pushHistory();
    const next = cloneSlides(slides);
    const live = next[activeSlideIndex].elements.filter((e) => selectedElementIds.includes(e.id) && !e.locked);
    live.sort((a, b) => (axis === 'horizontal' ? a.x - b.x : a.y - b.y));
    if (axis === 'horizontal') {
      const first = live[0]!;
      const last = live[live.length - 1]!;
      const span = last.x + last.w - first.x;
      const totalW = live.reduce((s, e) => s + e.w, 0);
      const gap = (span - totalW) / (live.length - 1);
      let x = first.x;
      for (const el of live) {
        el.x = Math.round(x);
        x += el.w + gap;
      }
    } else {
      const first = live[0]!;
      const last = live[live.length - 1]!;
      const span = last.y + last.h - first.y;
      const totalH = live.reduce((s, e) => s + e.h, 0);
      const gap = (span - totalH) / (live.length - 1);
      let y = first.y;
      for (const el of live) {
        el.y = Math.round(y);
        y += el.h + gap;
      }
    }
    set({ slides: next });
    scheduleSave(get);
  },

  duplicateStyles: () => {
    const { slides, activeSlideIndex, selectedElementIds, selectedElementId } = get();
    if (!selectedElementId || selectedElementIds.length < 2) return;
    const source = slides[activeSlideIndex]?.elements.find((e) => e.id === selectedElementId);
    if (!source || source.locked) return;
    const style: Partial<SlideElement> = {};
    for (const key of STYLE_KEYS) {
      if (source[key] !== undefined) (style as Record<string, unknown>)[key] = source[key];
    }
    get().pushHistory();
    const next = cloneSlides(slides);
    for (const el of next[activeSlideIndex].elements) {
      if (el.id === selectedElementId || el.locked || !selectedElementIds.includes(el.id)) continue;
      Object.assign(el, style);
    }
    set({ slides: next });
    scheduleSave(get);
  },

  setSlideNotes: (notes) => {
    get().pushHistory('notes');
    const { slides, activeSlideIndex } = get();
    const next = cloneSlides(slides);
    next[activeSlideIndex].notes = notes;
    set({ slides: next });
    scheduleSave(get);
  },

  applyLayout: (layoutId) => {
    get().pushHistory();
    const { slides, activeSlideIndex } = get();
    const next = cloneSlides(slides);
    next[activeSlideIndex] = switchLayout(next[activeSlideIndex], layoutId, activeSlideIndex);
    set({ slides: next, ...selectionState([]) });
    scheduleSave(get);
  },

  applySlideAi: async (action) => {
    const { slides, activeSlideIndex } = get();
    const slide = slides[activeSlideIndex];
    if (!slide) return;
    const payload = slideToPayload(slide);
    const res = await api.editSlide(action, {
      layoutId: slide.layoutId,
      notes: slide.notes,
      payload: payload as Record<string, unknown>,
    });
    get().pushHistory();
    const next = cloneSlides(slides);

    if (action === 'tighten') {
      next[activeSlideIndex] = applyPayloadToSlide(
        slide,
        activeSlideIndex,
        res.result as Partial<MagicSlidePayload>,
      );
    } else if (action === 'add_kpi') {
      const kpis = (res.result.kpis as { metric?: string; label?: string; delta?: string }[]) ?? [];
      const startX = 80;
      const startY = 200;
      kpis.slice(0, 3).forEach((k, i) => {
        const card = createSmartBlock('kpi_card', { x: startX + i * 300, y: startY });
        card.metric = k.metric || '—';
        card.label = k.label || 'Metric';
        card.delta = k.delta || '';
        next[activeSlideIndex].elements.push(card);
      });
    } else if (action === 'notes_to_bullets') {
      const bullets = (res.result.bullets as string[]) ?? [];
      const title = typeof res.result.title === 'string' ? res.result.title : undefined;
      const bulletText = bullets.map((b) => `• ${b.replace(/^[-•]\s*/, '')}`).join('\n');
      const texts = next[activeSlideIndex].elements.filter((e) => e.type === 'text' && !e.locked);
      if (title && texts[0]) texts[0].text = title;
      const body = texts.length > 1 ? texts[1] : texts[0];
      if (body && bulletText) body.text = bulletText;
      else if (bulletText) {
        next[activeSlideIndex].notes = bullets.join('\n');
      }
      if (!slide.notes?.trim() && bullets.length) {
        // Keep notes as the source that was converted when notes were empty.
      }
    }

    set({ slides: next, ...selectionState([]) });
    scheduleSave(get);
  },

  addSlide: (layoutId = 'exec_summary') => {
    get().pushHistory();
    const { slides } = get();
    const slide = buildLayout(layoutId, slides.length);
    const next = renumberSlides([...slides, slide]);
    set({ slides: next, activeSlideIndex: next.length - 1 });
    scheduleSave(get);
  },

  duplicateSlide: (index) => {
    get().pushHistory();
    const { slides, activeSlideIndex } = get();
    const i = index ?? activeSlideIndex;
    const copy = structuredClone(slides[i]);
    copy.id = uid('slide_');
    copy.elements = copy.elements.map((e) => ({ ...e, id: uid() }));
    const next = renumberSlides([
      ...slides.slice(0, i + 1),
      copy,
      ...slides.slice(i + 1),
    ]);
    set({ slides: next, activeSlideIndex: i + 1 });
    scheduleSave(get);
  },

  deleteSlide: (index) => {
    const { slides, activeSlideIndex } = get();
    if (slides.length <= 1) return;
    get().pushHistory();
    const i = index ?? activeSlideIndex;
    const next = renumberSlides(slides.filter((_, idx) => idx !== i));
    set({
      slides: next,
      activeSlideIndex: Math.min(i, next.length - 1),
      ...selectionState([]),
    });
    scheduleSave(get);
  },

  reorderSlides: (from, to) => {
    if (from === to) return;
    get().pushHistory();
    const { slides } = get();
    const next = [...slides];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    set({ slides: renumberSlides(next), activeSlideIndex: to });
    scheduleSave(get);
  },

  replaceSlides: (slides, title) => {
    get().pushHistory();
    set({
      slides: renumberSlides(slides),
      title: title ?? get().title,
      activeSlideIndex: 0,
      ...selectionState([]),
    });
    scheduleSave(get);
  },

  reapplyTemplate: () => {
    get().pushHistory();
    const { slides } = get();
    const next = slides.map((slide, i) => {
      const rebuilt = switchLayout(slide, slide.layoutId, i);
      rebuilt.notes = slide.notes;
      return rebuilt;
    });
    set({ slides: renumberSlides(next), ...selectionState([]) });
    scheduleSave(get);
  },

  syncSave: async (opts) => {
    const { deckId, title, slides, theme, visibility, category, canEdit, lastSavedAt } = get();
    if (!canEdit) return;
    set({ saveStatus: 'saving' });
    try {
      if (!deckId) {
        const created = await api.createDeck({ title, slides, theme, visibility, category });
        set({
          deckId: created.id,
          saveStatus: 'saved',
          lastSavedAt: created.updatedAt,
        });
        localStorage.setItem('slidesprint:lastDeckId', created.id);
      } else {
        const saved = await api.saveDeck(deckId, {
          title,
          slides,
          theme,
          visibility,
          category,
          expectedUpdatedAt: opts?.force ? undefined : lastSavedAt,
          saveVersion: opts?.saveVersion,
        });
        set({ saveStatus: 'saved', lastSavedAt: saved.updatedAt });
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        set({ saveStatus: 'conflict' });
        return;
      }
      // Offline / local-dev without wrangler — persist locally
      localStorage.setItem(
        'slidesprint:localDeck',
        JSON.stringify({ title, slides, theme, deckId }),
      );
      set({ saveStatus: 'offline' });
    }
  },

  reloadFromServer: async () => {
    const { deckId } = get();
    if (!deckId) return;
    await get().openDeck(deckId);
  },

  restoreVersion: async (versionId) => {
    const { deckId, canEdit } = get();
    if (!deckId || !canEdit) return;
    if (saveTimer) {
      clearTimeout(saveTimer);
      saveTimer = null;
    }
    const restored = await api.restoreVersion(deckId, versionId);
    lastHistory = null;
    set({
      title: restored.title,
      slides: restored.slides.length ? restored.slides : createStarterDeck(),
      activeSlideIndex: 0,
      ...selectionState([]),
      saveStatus: 'saved',
      lastSavedAt: restored.updatedAt,
      past: [],
      future: [],
    });
  },

  loadOrCreate: async () => {
    const localId = localStorage.getItem('slidesprint:lastDeckId');
    try {
      if (localId) {
        const deck = await api.getDeck(localId);
        set({
          deckId: deck.id,
          title: deck.title,
          slides: deck.slides.length ? deck.slides : createStarterDeck(),
          theme: deck.theme in THEMES ? deck.theme : 'ast_standard',
          saveStatus: 'saved',
          lastSavedAt: deck.updatedAt,
        });
        return;
      }
    } catch {
      /* fall through */
    }

    const cached = localStorage.getItem('slidesprint:localDeck');
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as {
          title: string;
          slides: Slide[];
          theme: string;
          deckId: string | null;
        };
        set({
          deckId: parsed.deckId,
          title: parsed.title,
          slides: parsed.slides,
          theme: parsed.theme,
          saveStatus: 'offline',
        });
        return;
      } catch {
        /* ignore */
      }
    }
  },

  openDeck: async (id) => {
    await flushSave(get);
    const deck = await api.getDeck(id);
    lastHistory = null;
    set({
      deckId: deck.id,
      title: deck.title,
      slides: deck.slides.length ? deck.slides : createStarterDeck(),
      theme: deck.theme in THEMES ? deck.theme : 'ast_standard',
      visibility: deck.visibility,
      category: deck.category,
      canEdit: deck.canEdit,
      isOwner: deck.isOwner ?? deck.canEdit,
      ownerName: deck.ownerName,
      activeSlideIndex: 0,
      ...selectionState([]),
      saveStatus: deck.canEdit ? 'saved' : 'idle',
      lastSavedAt: deck.updatedAt,
      past: [],
      future: [],
    });
    if (deck.canEdit) localStorage.setItem('slidesprint:lastDeckId', deck.id);
  },

  newDeck: async () => {
    await flushSave(get);
    lastHistory = null;
    localStorage.removeItem('slidesprint:lastDeckId');
    localStorage.removeItem('slidesprint:localDeck');
    set(freshDeckState());
    await get().syncSave();
    return get().deckId;
  },

  newDeckFromTemplate: async (slides, title, theme = 'ast_standard') => {
    await flushSave(get);
    lastHistory = null;
    localStorage.removeItem('slidesprint:lastDeckId');
    localStorage.removeItem('slidesprint:localDeck');
    const fresh = structuredClone(slides).map((s) => ({
      ...s,
      id: uid('slide_'),
      elements: s.elements.map((e) => ({ ...e, id: uid() })),
    }));
    set({
      ...freshDeckState(),
      title,
      theme: theme in THEMES ? theme : 'ast_standard',
      slides: renumberSlides(fresh),
    });
    await get().syncSave();
    return get().deckId;
  },

  duplicateDeck: async (id) => {
    await flushSave(get);
    const deck = await api.getDeck(id);
    const copy = await api.createDeck({
      title: `${deck.title} (copy)`,
      slides: deck.slides,
      theme: deck.theme,
      category: deck.category,
    });
    await get().openDeck(copy.id);
    return copy.id;
  },

  deleteDeck: async (id) => {
    await api.deleteDeck(id);
    if (get().deckId === id) {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = null;
      lastHistory = null;
      localStorage.removeItem('slidesprint:lastDeckId');
      set(freshDeckState());
    }
  },

  applyMagicDraft: async (notes, mode = 'replace') => {
    const { title } = get();
    const result = await api.magicDraft(notes, title);
    const hydrated = hydrateMagicDeck(result.title, result.slides);
    if (mode === 'replace') {
      get().replaceSlides(hydrated.slides, hydrated.title);
      return;
    }
    // Append: keep the existing cover/closing; insert only the drafted content slides.
    const added = hydrated.slides.filter((s) => s.layoutId !== 'title_slide' && s.layoutId !== 'closing');
    if (!added.length) throw new Error('The draft had no content slides to add.');
    get().pushHistory();
    const { slides, activeSlideIndex } = get();
    const at = activeSlideIndex + 1;
    set({
      slides: renumberSlides([...slides.slice(0, at), ...added, ...slides.slice(at)]),
      activeSlideIndex: at,
      ...selectionState([]),
    });
    scheduleSave(get);
  },
}));

function scheduleSave(get: () => DeckState) {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    void get().syncSave();
  }, 900);
}
